/**
 * The Hire window, Edit Employee and Former Employees (piece 34, *The Hire
 * window*; decision 3181). 🔒 Hire on HR › Employees and + Hire on our own
 * Company's Employees tile open one window in five sections, Person, Job,
 * Place, Pay and Access, and the hire is one transaction. 🔨 The hire and Edit
 * Employee are gated on `hr.hire`; Edit Employee writes a dated position, never
 * an overwrite.
 */

import type { HomeAddressDraft } from './employeeFile';
import type { InviteDeliveryFacts } from '../identity/logins';
import { hasDayShape } from '../zonedTime';

/** The page that draws HR › Employees with the Hire window open; the three answers of decision 2690 carry it. */
export const HIRE_WINDOW_ROUTE = '/hr/hire';
/** The hire, written in one transaction. */
export const HR_HIRE_PATH = '/hr/hire';
/** What the window's pickers list: job roles, departments, managers, levels and our own Company's sites. */
export const HR_HIRE_OPTIONS_PATH = '/hr/hire/options';
/** The people on file under the name the Person section was given. */
export const HR_HIRE_MATCHES_PATH = '/hr/hire/matches';
/** Every job at our own Company that has ended, the Employee list's second view. */
export const HR_FORMER_EMPLOYEES_PATH = '/hr/former-employees';

/** Edit Employee's write: a dated position on the job. */
export function employeeEditPath(employmentId: string): string {
  return `/hr/employees/${encodeURIComponent(employmentId)}/edit`;
}

/** 🔨 The key the hire and Edit Employee ask, seeded on HR and Admin. */
export const HR_HIRE_CAPABILITY = 'hr.hire';

/** The refusal the hire and Edit Employee answer a viewer without the key, in a sentence. */
export const HIRE_REFUSAL = 'Hiring and editing an employee are open to a holder of the hire permission, and this identity holds none.';
/** 🔒 The refusal of HR's other writes, the required trainings and HR Rules, which ask the same key (decision 3182). */
export const HR_RULES_REFUSAL = 'Changing the required trainings and HR Rules is open to a holder of the hire permission, and this identity holds none.';

/** The first pay row's reason, and the first position's. */
export const HIRE_REASON = 'Hire';

export const WORK_SCHEDULES = ['FullTime', 'PartTime'] as const;
export type WorkScheduleName = (typeof WORK_SCHEDULES)[number];
export const PAY_BASES = ['Hourly', 'Salaried'] as const;
export type PayBasisName = (typeof PAY_BASES)[number];
export const FLSA_CLASSIFICATIONS = ['NonExempt', 'Exempt'] as const;
export type FlsaClassificationName = (typeof FLSA_CLASSIFICATIONS)[number];
export const PAY_RATE_UNITS = ['PerHour', 'PerYear'] as const;
export type PayRateUnitName = (typeof PAY_RATE_UNITS)[number];

/**
 * 🔒 The Access section's answers (T3007): no login, or an invitation on one of the five channels the Invite New User
 * modal offers, the work email, a personal address, a mobile code, a code handed over in person or a temporary password.
 */
export const HIRE_LOGIN_CHOICES = ['none', 'WorkEmail', 'PersonalEmail', 'Mobile', 'InPerson', 'TemporaryPassword'] as const;

/** 🔒 The two choices whose code or password is shown once after the hire is saved, in the hand-over window of decision 0482. */
export function hireLoginHandsOver(choice: HireLoginChoice): boolean {
  return choice === 'InPerson' || choice === 'TemporaryPassword';
}

/** A choice's string key: the prefix and the stored member in dotted lower camel case, `hr.basis.hourly` for `Hourly`. */
export function choiceLabelKey(prefix: string, value: string): string {
  return `${prefix}.${value.charAt(0).toLowerCase()}${value.slice(1)}`;
}
export type HireLoginChoice = (typeof HIRE_LOGIN_CHOICES)[number];

/** The Job section, which Edit Employee asks again. */
export interface HireJobDraft {
  title: string;
  jobRoleId: string;
  departmentId: string;
  /** Empty where the department's head is the manager. */
  bossEmploymentId: string;
  skillLevelId: string;
  workSchedule: WorkScheduleName | '';
  basis: PayBasisName | '';
  flsaClassification: FlsaClassificationName | '';
}

/** The whole window, as the browser sends it. */
export interface HireDraft extends HireJobDraft {
  /** A match picked in the Person section; empty, the name below is a new person. */
  personId: string;
  firstName: string;
  lastName: string;
  /** How many people were shown when the hirer answered *No, this is a new person*. */
  matchesShown: number;
  /** The first day, as an ISO calendar day. */
  startDate: string;
  workLocationSiteId: string;
  workEmail: string;
  payRate: string;
  payRateUnit: PayRateUnitName | '';
  hoursPerPayPeriod: string;
  login: HireLoginChoice;
  /** The personal address a PersonalEmail invitation goes to, or the number a Mobile code goes to. */
  loginDestination: string;
  /** 🔒 The job's private home address (decision 3182); left empty, nothing is written. */
  home?: HomeAddressDraft;
}

/** Edit Employee: the Job section, the work site and the day it takes effect. */
export interface EmployeeEditDraft extends HireJobDraft {
  /** 🔒 The work site, dated on the position (decision 3782); one of our own Company's sites. */
  workLocationSiteId: string;
  effectiveOn: string;
}

/** Edit Employee's starting values: the position in force, its work site included. */
export type EmployeeEditStart = Omit<EmployeeEditDraft, 'effectiveOn'>;

export const EMPTY_HIRE_JOB: HireJobDraft = {
  title: '', jobRoleId: '', departmentId: '', bossEmploymentId: '', skillLevelId: '', workSchedule: 'FullTime', basis: '', flsaClassification: '',
};

const ADDRESS = /^[^\s@]+@[^\s@]+$/;
/** The number shape the invitation's own delivery accepts. */
const MOBILE = /^\+?[0-9][0-9 ()-]{6,}$/;

/** A rate the pay row holds: a positive amount with at most two places. */
export function payRateProblem(text: string): string | null {
  if (!/^\d+(\.\d{1,2})?$/.test(text.trim())) return 'The pay rate is an amount with at most two decimal places.';
  return Number(text) > 0 ? null : 'The pay rate is more than nothing.';
}

/** The Job section's own problems, each one sentence; empty when it saves. */
export function hireJobProblems(job: HireJobDraft): string[] {
  const problems: string[] = [];
  if (!job.title.trim()) problems.push('The job needs a title.');
  if (!job.jobRoleId) problems.push('The job needs a job role.');
  if (!job.departmentId) problems.push('The job needs a department.');
  if (!job.workSchedule) problems.push('The job is full time or part time.');
  if (!job.basis) problems.push('The job is hourly or salaried.');
  if (!job.flsaClassification) problems.push('The job is exempt or non-exempt from overtime.');
  return problems;
}

/** 🔒 The whole hire's problems, which the window and the route both ask, so a refusal reads the same in either. */
export function hireProblems(draft: HireDraft): string[] {
  const problems: string[] = [];
  if (!draft.personId && (!draft.firstName.trim() || !draft.lastName.trim())) problems.push('The hire picks a person on file or names a new one, first and last.');
  if (!hasDayShape(draft.startDate)) problems.push('The hire needs a first day.');
  problems.push(...hireJobProblems(draft));
  if (!draft.workLocationSiteId) problems.push('The hire needs a work site.');
  if (!ADDRESS.test(draft.workEmail.trim())) problems.push('The hire needs a work email.');
  const rate = payRateProblem(draft.payRate);
  if (rate) problems.push(rate);
  if (!draft.payRateUnit) problems.push('The pay rate is per hour or per year.');
  if (draft.hoursPerPayPeriod.trim() && !/^\d+(\.\d{1,2})?$/.test(draft.hoursPerPayPeriod.trim())) problems.push('The hours a pay period are a number.');
  if (!(HIRE_LOGIN_CHOICES as readonly string[]).includes(draft.login)) problems.push('The login is one of the choices the Access section offers.');
  if (draft.login === 'PersonalEmail' && !ADDRESS.test(draft.loginDestination.trim())) problems.push('A personal invitation needs the personal address it goes to.');
  if (draft.login === 'Mobile' && !MOBILE.test(draft.loginDestination.trim())) problems.push('A mobile invitation needs the number the code goes to.');
  // A home address left wholly empty writes nothing; a partial one is refused rather than half-written.
  const home = draft.home;
  if (home && (home.addressLines.some((line) => line.trim() !== '') || home.locality.trim() !== '' || home.postalCode.trim() !== '')) problems.push(...homeAddressProblems(home));
  return problems;
}

/** 🔒 A home address's problems (decision 3182): a street line, a city and a two-letter country; the window and the route both ask. */
export function homeAddressProblems(draft: HomeAddressDraft): string[] {
  const problems: string[] = [];
  if (!draft.addressLines.some((line) => line.trim() !== '')) problems.push('The home address needs a street line.');
  if (!draft.locality.trim()) problems.push('The home address needs a city.');
  if (!/^[A-Za-z]{2}$/.test(draft.countryCode.trim())) problems.push('The home address needs a two-letter country.');
  return problems;
}

/** Edit Employee's problems: the Job section, a work site and a day today or earlier. */
export function employeeEditProblems(draft: EmployeeEditDraft, today: string): string[] {
  const problems = hireJobProblems(draft);
  if (!draft.workLocationSiteId) problems.push('The job needs a work site.');
  if (!hasDayShape(draft.effectiveOn)) problems.push('The change needs the day it takes effect.');
  else if (draft.effectiveOn > today) problems.push('A change takes effect today or earlier, never on a future day.');
  return problems;
}

/** One choice a picker lists. */
export interface HireChoice {
  id: string;
  name: string;
}

/** A job role as the window lists it, with the defaults it fills. */
export interface HireJobRoleChoice extends HireChoice {
  defaultBasis: PayBasisName;
  defaultFlsaClassification: FlsaClassificationName;
}

/** What the window's pickers list. */
export interface HireOptions {
  jobRoles: HireJobRoleChoice[];
  departments: HireChoice[];
  /** Every current job at our own Company, by its employment. */
  managers: HireChoice[];
  levels: HireChoice[];
  /** Our own Company's sites, one of which is the work location. */
  sites: HireChoice[];
  /** 🔒 Whether mail and texts are set up (T3007); absent, every choice is offered and the hire's own refusal answers. */
  delivery?: InviteDeliveryFacts;
}

/** One person on file under the name given. */
export interface HireMatch {
  personId: string;
  name: string;
  /** Where they work today, or nothing. */
  detail: string | null;
}

/** The hire's answer. */
export interface HireAnswer {
  employmentId: string;
  personId: string;
  /** Whether an invitation was sent with the hire. */
  invited: boolean;
  /** 🔒 The in-person code or the temporary password, shown once in the hand-over window (decision 0482); null on every other choice. */
  handOver: string | null;
}

/** A job role's defaults, filled into the Job section as it is chosen; the hirer changes either before the save. */
export function withRoleDefaults<T extends HireJobDraft>(job: T, role: HireJobRoleChoice | undefined): T {
  if (!role) return { ...job, jobRoleId: '' };
  return { ...job, jobRoleId: role.id, basis: role.defaultBasis, flsaClassification: role.defaultFlsaClassification };
}
